import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Field, getFormSyncErrors, getFormValues, initialize, reduxForm } from "redux-form";
import { Button, Modal, ReduxControls, Layout, ProgressIndicator, ErrorBanner } from "rhombus-react";
import { useDispatch, useSelector } from "react-redux";
import { noop } from "utils/common";

import { CREATE_BLANK_IMPOSIUM_FORM } from "widgets/forms/list";
import { createBlankImposiumCreative } from "reducers/creatives/VAST";
import { inRange, requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";

import "./CreateBlankImposiumModal.css";

const DIMENSION_OPTIONS = [
	{
		value: "1920x1080",
		label: "1920x1080 (Horizontal)",
	},
	{
		value: "1080x1920",
		label: "1080x1920 (Vertical)",
	},
	{
		value: "1080x1080",
		label: "1080x1080 (Square)",
	},
	{
		value: "custom",
		label: "Custom",
	}
];

const DURATION_OPTIONS = [
	{
		label: "6 seconds",
		value: 6,
	},
	{
		label: "15 seconds",
		value: 15,
	},
	{
		label: "30 seconds",
		value: 30,
	},
	{
		label: "1 minute",
		value: 60,
	},
	{
		label: "Custom duration",
		value: "custom",
	},
];

const FRAMERATE_OPTIONS = [
	{
		label: "23.976 fps",
		value: 23.976,
	},
	{
		label: "24 fps",
		value: 24,
	},
	{
		label: "25 fps",
		value: 25,
	},
	{
		label: "29.98 fps",
		value: 29.98,
	},
	{
		label: "30 fps",
		value: 30,
	},
	{
		label: "60 fps",
		value: 60,
	},
];

const DEFAULT_FORM_VALUES = {
	dimension: { width: 1920, height: 1080 },
	duration: { label: "15 seconds", value: 15 },
	customDuration: 30,
	frameRate: 	{ label: "24 fps", value: 24 },
};

const BLANK_IMPOSIUM_CONSTRAINTS = {
	duration: {
		min: 1,
		max: 120,
	},
	dimensions: {
		min: 100,
		max: 4096,
	},
};

const customDimensionRange = inRange(BLANK_IMPOSIUM_CONSTRAINTS.dimensions);
const customDurationRange = inRange(BLANK_IMPOSIUM_CONSTRAINTS.duration);

/**
 * Formats formValues into the proper object before being submitted to the api
 * @param {object} values redux form values
 */
export function formatFormValues(values = {}) {
	// We need to format formValues into the proper object before being sent to the api
	const {
		name,
		dimension = {},
		duration = {},
		frameRate = {},
		customDimension,
		customDuration,
	} = values;

	let formattedValues = { name };

	if (dimension === "custom") {
		const { width, height } = customDimension || {};

		// Ensure both width, height are passed or exclude it from the formatted field
		if (width && height) {
			formattedValues = { ...formattedValues, width, height };
		}
	} else {
		const { width, height } = dimension;
		formattedValues = { ...formattedValues, width, height };
	}

	// Normalize duration/frameRate values here since initializing
	// default values while using normailze in Select Fields causes issues with the label

	if (duration.value === "custom") {
		// Ensure customDuration is passed or exclude it from the formatted field
		if (customDuration) {
			formattedValues = { ...formattedValues, duration: customDuration };
		}
	} else {
		formattedValues = { ...formattedValues, duration: duration.value };
	}

	formattedValues = { ...formattedValues, frameRate: frameRate.value };

	return formattedValues;
}

function CreateBlankImposiumModal({
	libraryId,
	commonModalProps,
	onClose,
	handleSubmit,
	navigationActions: { pushModal, popModal },
}) {
	const dispatch = useDispatch();
	const formValues = useSelector(getFormValues(CREATE_BLANK_IMPOSIUM_FORM)) || {};
	const syncErrors = useSelector(getFormSyncErrors(CREATE_BLANK_IMPOSIUM_FORM)) || {};
	const [isSaving, setIsSaving] = useState(false);
	const [errorObj, setErrorObj] = useState({});
	const { dimension, customDimension, duration, customDuration } = formValues;
	const isCustomDimension = dimension === "custom";
	const isCustomDuration = duration?.value === "custom";

	// Check if dimensions is pristine or not to determine whether to update the field's required asterik
	const isDimensionsValid = isCustomDimension ? !!customDimension?.width && !!customDimension?.height : !!dimension;
	const isDurationValid = isCustomDuration ? !!customDuration : !!duration;

	useEffect(() => {
		dispatch(initialize(CREATE_BLANK_IMPOSIUM_FORM, DEFAULT_FORM_VALUES));
	}, []);

	const handleFormSubmit = (handleSubmit((values) => {
		// We need to format formValues into the proper object before being sent to the api
		const formattedValues = formatFormValues(values);

		setErrorObj({});
		setIsSaving(true);

		dispatch(createBlankImposiumCreative(libraryId, formattedValues)).then((resp) => {
			if (resp.success) {
				onClose();
			}

			setErrorObj(resp.payload.errorObj);
			setIsSaving(false);
		});
		// Dispatch parsed values to api
	}));

	const Body = () => {
		return (
			<form id={CREATE_BLANK_IMPOSIUM_FORM} onSubmit={handleFormSubmit}>
				<ProgressIndicator key="loading" isLoading={isSaving} />
				{ !!Object.keys(errorObj).length && <ErrorBanner
					title="Sorry, please fix the errors below and try again."
					messages={[errorObj.description]} />
				}
				<Field
					name="name"
					className="creative-name cypress-imposium-creative-name-field"
					label="Creative Name"
					required
					validate={[requiredNotNull, creativeNameAllowedChars, creativeNameMaxLength]}
					component={ReduxControls.Input}
				/>
				<div className="item-row">
					<div className="dimension-options">
						<label
							form="dimension-options"
							className={classNames({
								"dimension-label": true,
								required: true,
								pristine: !isDimensionsValid,
								valid: isDimensionsValid,
							})}>
							Video Dimensions
						</label>
						{DIMENSION_OPTIONS.map((option) => {
							const { value, label } = option;

							return (
								<Field
									key={value}
									name="dimension"
									className={`cypress-imposium-dimension-field-${value}`}
									label={label}
									value={value}
									required
									normalize={(value) => {
										if (value === "custom") {
											return value;
										}

										const [width, height] = value.split("x");
										return { width: Number(width), height: Number(height) };
									}}
									format={(value) => {
										if (value === "custom") {
											return value;
										}

										return `${value?.width}x${value?.height}`;
									}}
									useValue
									component={ReduxControls.Checkbox}
									type="radio"
								/>
							);
						})}
						{ isCustomDimension && <>
							<div className="custom-dimensions">
								<Field
									name="customDimension.width"
									className="cypress-imposium-custom-dimension-width-field"
									type="number"
									placeholder="Width"
									required={isCustomDimension}
									validate={isCustomDimension ?[requiredNotNull, customDimensionRange] : undefined}
									normalize={value => Number(value)}
									component={ReduxControls.Input}
									{...BLANK_IMPOSIUM_CONSTRAINTS.dimensions}
								/>
								<span>x</span>
								<Field
									name="customDimension.height"
									className="cypress-imposium-custom-dimension-height-field"
									type="number"
									placeholder="Height"
									required={isCustomDimension}
									validate={isCustomDimension ?[requiredNotNull, customDimensionRange] : undefined}
									normalize={value => Number(value)}
									component={ReduxControls.Input}
									{...BLANK_IMPOSIUM_CONSTRAINTS.dimensions}
								/>
							</div>
						</>
						}
					</div>
					<div className="item-column">
						<div className="duration-options">
							<Field
								name="duration"
								label="Duration"
								className={classNames({
									"duration-field cypress-imposium-duration-field": true,
									pristine: !isDurationValid,
								})}
								isClearable={false}
								required
								validate={requiredNotNull}
								component={ReduxControls.Select}
								options={DURATION_OPTIONS}
								onMenuScrollToBottom={noop}
							/>

							{ isCustomDuration && <>
								<Field
									name="customDuration"
									className="custom-duration cypress-imposium-custom-duration-field"
									type="number"
									normalize={value => Number(value)}
									required={isCustomDuration}
									validate={isCustomDimension ?[requiredNotNull, customDurationRange] : undefined}
									component={ReduxControls.Input}
								/>
								<span>sec</span>
							</>}
						</div>
						<Field
							name="frameRate"
							label="Frame Rate"
							className="framerate-field cypress-imposium-framerate-field"
							isClearable={false}
							required
							component={ReduxControls.Select}
							options={FRAMERATE_OPTIONS}
							validate={requiredNotNull}
							onMenuScrollToBottom={noop}
						/>
					</div>
				</div>
			</form>
		);
	};

	const Footer = () => {
		return (
			<Layout.ModalFooter>
				<Button
					className="cypress-imposium-back"
					theme="default"
					onClick={popModal}>
					Back
				</Button>
				<Button
					disabled={isSaving || Object.keys(syncErrors).length}
					className="cypress-imposium-save-vast"
					type="submit"
					theme="green"
					form={CREATE_BLANK_IMPOSIUM_FORM}>
					Create VAST Creative
				</Button>
			</Layout.ModalFooter>
		);
	};

	return (
		<Modal
			{...commonModalProps}
			renderContent={Body}
			renderFooter={Footer}
			className="create-blank-imposium"
			title="Create VAST - Dynamic - Imposium (Advanced Video)"
		/>
	);
}

export default reduxForm({
	form: CREATE_BLANK_IMPOSIUM_FORM,
})(CreateBlankImposiumModal);
